import { useState, useEffect, useMemo, useCallback } from 'react';
import { collection, onSnapshot, orderBy, query } from 'firebase/firestore';

import { db } from '../../../firebase/config';
import { toRow, sortRows, filterRows, clampWidth, mergeWidths } from './prospectosWebModel';

export const PROSPECTOS_WEB_PATH = 'public/data/prospectos_web';
const PAGE_SIZE = 25;

/** Clave del navegador donde cada usuario guarda sus anchos de columna. */
const WIDTHS_STORAGE_KEY = 'prospectos-web:column-widths';

/** Anchos iniciales, pensados para el contenido típico de cada columna. */
const DEFAULT_WIDTHS = {
    fechaCreacion: 110,
    displayNombre: 220,
    documento: 110,
    telefono: 140,
    email: 240,
    vehiculo: 240,
    localidad: 170,
    cantidadCotizaciones: 120,
    dispositivo: 120
};

export const ACTIONS_COLUMN_WIDTH = 90;

/**
 * Lee los anchos guardados. El storage puede fallar (modo privado, permisos) o tener basura
 * de una versión anterior: en cualquier caso se cae a los valores por defecto.
 */
function readStoredWidths() {
    try {
        return JSON.parse(window.localStorage.getItem(WIDTHS_STORAGE_KEY));
    } catch {
        return null;
    }
}

function persistWidths(widths) {
    try {
        window.localStorage.setItem(WIDTHS_STORAGE_KEY, JSON.stringify(widths));
    } catch {
        // Que no se puedan guardar los anchos no es motivo para romper la pantalla.
    }
}

export const COLUMN_LABELS = {
    fechaCreacion: 'Fecha',
    displayNombre: 'Visitante',
    documento: 'DNI',
    telefono: 'Teléfono',
    email: 'Email',
    vehiculo: 'Vehículo',
    localidad: 'Localidad',
    cantidadCotizaciones: 'Cotizaciones',
    dispositivo: 'Dispositivo'
};

const DEFAULT_COLUMNS = {
    fechaCreacion: true,
    displayNombre: true,
    documento: false,
    telefono: true,
    email: true,
    vehiculo: true,
    localidad: true,
    cantidadCotizaciones: true,
    dispositivo: false
};

/**
 * Cerebro de negocio del tablero de Prospectos Web.
 *
 * Escucha en vivo la colección de leads del cotizador, los aplana a filas y resuelve
 * búsqueda, orden y paginación en memoria. El volumen esperado (leads de un cotizador
 * web) entra holgado en el cliente, y hacerlo en memoria evita pedirle a Firestore un
 * índice compuesto por cada combinación de orden y filtro.
 */
export const useProspectosWebTable = () => {
    const [leads, setLeads] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);

    const [searchTerm, setSearchTerm] = useState('');
    const [sortConfig, setSortConfig] = useState({ key: 'fechaCreacion', direction: 'descending' });
    const [columnVisibility, setColumnVisibility] = useState(DEFAULT_COLUMNS);
    const [currentPage, setCurrentPage] = useState(1);
    const [selectedLead, setSelectedLead] = useState(null);
    const [columnWidths, setColumnWidths] = useState(() => mergeWidths(DEFAULT_WIDTHS, readStoredWidths()));

    useEffect(() => persistWidths(columnWidths), [columnWidths]);

    useEffect(() => {
        const q = query(collection(db, PROSPECTOS_WEB_PATH), orderBy('fechaCreacion', 'desc'));

        const unsubscribe = onSnapshot(
            q,
            (snapshot) => {
                setLeads(snapshot.docs.map((doc) => toRow({ id: doc.id, ...doc.data() })));
                setError(null);
                setLoading(false);
            },
            (err) => {
                console.error('[ProspectosWeb] Error al leer la colección:', err);
                setError(err.message);
                setLoading(false);
            }
        );

        return () => unsubscribe();
    }, []);

    const filteredRows = useMemo(() => filterRows(leads, searchTerm), [leads, searchTerm]);
    const sortedRows = useMemo(() => sortRows(filteredRows, sortConfig), [filteredRows, sortConfig]);

    const totalPages = Math.max(1, Math.ceil(sortedRows.length / PAGE_SIZE));

    // Si el filtro achica el resultado, la página actual puede quedar fuera de rango.
    const safePage = Math.min(currentPage, totalPages);

    const pageRows = useMemo(
        () => sortedRows.slice((safePage - 1) * PAGE_SIZE, safePage * PAGE_SIZE),
        [sortedRows, safePage]
    );

    const handleSort = useCallback((key) => {
        // 'ascending' / 'descending' es el vocabulario que espera SortableHeader para
        // decidir hacia dónde apunta la flecha.
        setSortConfig((prev) => ({
            key,
            direction: prev.key === key && prev.direction === 'ascending' ? 'descending' : 'ascending'
        }));
        setCurrentPage(1);
    }, []);

    /**
     * Arranca el arrastre de un borde de columna.
     *
     * Los listeners van en window y no en el th: si el puntero sale de la cabecera mientras
     * se arrastra —que es lo normal al agrandar— el th deja de recibir eventos y la columna
     * se quedaría trabada a mitad de camino.
     */
    const startResize = useCallback((key, event) => {
        event.preventDefault();
        event.stopPropagation();

        const startX = event.clientX;
        const startWidth = columnWidths[key];

        const onMove = (moveEvent) => {
            const nuevo = clampWidth(startWidth + (moveEvent.clientX - startX));
            if (nuevo !== null) setColumnWidths((prev) => ({ ...prev, [key]: nuevo }));
        };

        const onUp = () => {
            window.removeEventListener('mousemove', onMove);
            window.removeEventListener('mouseup', onUp);
            document.body.style.cursor = '';
            document.body.style.userSelect = '';
        };

        window.addEventListener('mousemove', onMove);
        window.addEventListener('mouseup', onUp);
        // Mientras se arrastra, el cursor y la ausencia de selección son globales: si no,
        // el navegador selecciona texto de la tabla y el arrastre se siente roto.
        document.body.style.cursor = 'col-resize';
        document.body.style.userSelect = 'none';
    }, [columnWidths]);

    const resetColumnWidths = useCallback(() => setColumnWidths(DEFAULT_WIDTHS), []);

    const toggleColumn = useCallback((key) => {
        setColumnVisibility((prev) => ({ ...prev, [key]: !prev[key] }));
    }, []);

    const handleSearch = useCallback((term) => {
        setSearchTerm(term);
        setCurrentPage(1);
    }, []);

    return {
        loading,
        error,
        rows: pageRows,
        totalRows: sortedRows.length,
        totalLeads: leads.length,
        searchTerm,
        handleSearch,
        sortConfig,
        handleSort,
        columnVisibility,
        toggleColumn,
        columnLabels: COLUMN_LABELS,
        currentPage: safePage,
        totalPages,
        setCurrentPage,
        selectedLead,
        setSelectedLead,
        columnWidths,
        startResize,
        resetColumnWidths
    };
};
